import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Clock3, Users, Video } from "lucide-react";
import { getErrorMessage } from "@/lib/errorMessages";
import { handleRateLimitError } from "@/lib/rateLimitHandler";
import { getMeetingCodeCandidates, isValidMeetingCode } from "@/lib/meetingUtils";
import { useTranslation } from "react-i18next";
import { JoinAIPrep } from "@/components/meetings/JoinAIPrep";
import { safeRandomUUID } from "@/utils/id";

type MeetingLookup = {
  id: string;
  title: string | null;
  meeting_code: string;
  waiting_room_enabled: boolean;
  is_active: boolean;
  has_password: boolean;
  scheduled_start: string | null;
};

const fetchMeetingByCode = async (meetingCode: string) => {
  const codeCandidates = getMeetingCodeCandidates(meetingCode);

  // Prefer the RPC for business logic (password, scheduling awareness)
  for (const code of codeCandidates) {
    const { data, error } = await supabase
      .rpc('get_meeting_by_code', { _meeting_code: code })
      .maybeSingle();

    if (data) {
      return { data: data as MeetingLookup, error: null };
    }

    if (error && error.code !== "PGRST116") {
      console.error("Meeting RPC lookup error:", error);
    }
  }

  // Fallback to a direct table query if the RPC isn't available in the current
  // environment so users can still join with their link instead of hitting an
  // opaque error.
  for (const code of codeCandidates) {
    const { data: tableData, error: tableError } = await supabase
      .from('meetings')
      .select('id, title, meeting_code, waiting_room_enabled, is_active, password, scheduled_start')
      .eq('meeting_code', code)
      .maybeSingle();

    if (tableData) {
      const meeting: MeetingLookup = {
        id: tableData.id,
        title: tableData.title,
        meeting_code: tableData.meeting_code,
        waiting_room_enabled: tableData.waiting_room_enabled,
        is_active: tableData.is_active,
        has_password: Boolean((tableData as any).password),
        scheduled_start: tableData.scheduled_start,
      };

      return { data: meeting, error: null };
    }

    if (tableError && tableError.code !== "PGRST116") {
      return { data: null, error: tableError };
    }
  }

  return { data: null, error: null };
};

export default function JoinMeeting() {
  const { t } = useTranslation();
  const { meetingCode: codeFromUrl } = useParams<{ meetingCode?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [meetingCode, setMeetingCode] = useState(codeFromUrl || searchParams.get("code") || "");
  const [password, setPassword] = useState("");
  const [guestName, setGuestName] = useState("");
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [waitingStatus, setWaitingStatus] = useState<{ meetingId: string; meetingTitle?: string; meetingCode?: string } | null>(null);
  const [waitingMessage, setWaitingMessage] = useState<string | null>(null);
  const [waitingTitle, setWaitingTitle] = useState<string>(t("joinMeeting.waitingRoomTitle"));
  const [admissionChannel, setAdmissionChannel] = useState<ReturnType<typeof supabase.channel> | null>(null);
  const [waitingError, setWaitingError] = useState<string | null>(null);
  const [pollIntervalId, setPollIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [activationPollId, setActivationPollId] = useState<NodeJS.Timeout | null>(null);
  useEffect(() => {
    checkUser();
    restoreAvSettings();

    return () => {
      stopPreview();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
      }
      if (activationPollId) {
        clearInterval(activationPollId);
      }
      if (admissionChannel) {
        supabase.removeChannel(admissionChannel);
      }
    };
  }, [pollIntervalId, activationPollId, admissionChannel]);

  const getJoinErrorDetails = (error: any) => {
    const status = error?.status ?? error?.cause?.status;
    const code = error?.code;
    const message = (error?.message || "").toLowerCase();

    if (status === 401 || status === 403) {
      return {
        title: t("joinMeeting.errors.signInTitle"),
        description: t("joinMeeting.errors.signInDesc"),
      };
    }

    if (status === 404 || message.includes("not found")) {
      return {
        title: t("joinMeeting.errors.notFoundTitle"),
        description: t("joinMeeting.errors.notFoundDesc"),
      };
    }

    if (status === 422 || code === "PGRST116") {
      return {
        title: t("joinMeeting.errors.invalidRequestTitle"),
        description: t("joinMeeting.errors.invalidRequestDesc"),
      };
    }

    if (message.includes("permission")) {
      return {
        title: t("joinMeeting.errors.permissionTitle"),
        description: t("joinMeeting.errors.permissionDesc"),
      };
    }

    return getErrorMessage(error);
  };

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setUser(session?.user || null);
  };

  const persistAvSettings = (settings: Record<string, any>) => {
    const existing = JSON.parse(localStorage.getItem("joinAvSettings") || "{}");
    localStorage.setItem("joinAvSettings", JSON.stringify({ ...existing, ...settings }));
  };

  const restoreAvSettings = () => {
    const stored = localStorage.getItem("joinAvSettings");
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed.videoOn === "boolean") setIsVideoOn(parsed.videoOn);
      if (typeof parsed.audioMuted === "boolean") setIsMuted(parsed.audioMuted);
    } catch (error) {
      console.warn("Unable to parse AV settings", error);
    }
  };

  const stopPreview = () => {
    previewStream?.getTracks().forEach((track) => track.stop());
    setPreviewStream(null);
  };

  const startPreview = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) return;
      stopPreview();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: isVideoOn,
        audio: true,
      });
      setPreviewStream(stream);

      stream.getVideoTracks().forEach((track) => {
        track.enabled = isVideoOn;
      });
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !isMuted;
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        const playPromise = videoRef.current.play();

        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch((error: unknown) => {
            const err = error as { name?: string };

            if (err?.name === "AbortError") return;

            console.error("Preview play failed", error);
            setInlineErrors((prev) => ({ ...prev, preview: t("joinMeeting.errors.previewStart") }));
          });
        }
      }
    } catch (error) {
      console.error("Preview start failed", error);
      setInlineErrors((prev) => ({ ...prev, preview: t("joinMeeting.errors.previewAccess") }));
    }
  };

  useEffect(() => {
    startPreview();
    persistAvSettings({
      videoOn: isVideoOn,
      audioMuted: isMuted,
    });
  }, [isVideoOn, isMuted]);

  useEffect(() => {
    if (videoRef.current && previewStream) {
      videoRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  const getOrCreateGuestId = () => {
    const storedGuest = sessionStorage.getItem("guestUserId");
    if (storedGuest) return storedGuest;
    const newGuestId = safeRandomUUID();
    sessionStorage.setItem("guestUserId", newGuestId);
    return newGuestId;
  };

  const subscribeToAdmission = (meetingId: string, userId: string, nextPath: string) => {
    const channel = supabase
      .channel(`waiting-room-${meetingId}-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "waiting_room_queue",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const status = (payload.new as any).status;
          if (status === "admitted") {
            navigate(nextPath);
          }
          if (status === "denied") {
            setWaitingError(t("joinMeeting.errors.hostDenied"));
          }
        }
      )
      .subscribe();

    setAdmissionChannel(channel);
  };

  const pollAdmission = (meetingId: string, userId: string, nextPath: string) => {
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from("waiting_room_queue")
        .select("status")
        .eq("meeting_id", meetingId)
        .eq("user_id", userId)
        .maybeSingle();

      if (data?.status === "admitted") {
        clearInterval(interval);
        navigate(nextPath);
      }
      if (data?.status === "denied") {
        clearInterval(interval);
        setWaitingError(t("joinMeeting.errors.hostDenied"));
      }
    }, 4000);
    setPollIntervalId(interval);
  };

  const clearActivationPolling = () => {
    if (activationPollId) {
      clearInterval(activationPollId);
      setActivationPollId(null);
    }
  };

  const proceedToMeeting = async (meetingDetails: MeetingLookup) => {
    clearActivationPolling();

    const nextPath = !user && guestName.trim()
      ? `/meeting/${meetingDetails.id}?guestName=${encodeURIComponent(guestName.trim())}`
      : `/meeting/${meetingDetails.id}`;

    setInlineErrors({});
    setLoading(false);
    persistAvSettings({ videoOn: isVideoOn, audioMuted: isMuted });

    if (meetingDetails.waiting_room_enabled) {
      setWaitingTitle(t("joinMeeting.waitingRoomTitle"));
      setIsWaiting(true);
      setWaitingStatus({ meetingId: meetingDetails.id, meetingTitle: meetingDetails.title || t("joinMeeting.meetingLabel"), meetingCode: meetingDetails.meeting_code });
      setWaitingMessage(t("joinMeeting.waitingRoomMessage"));

      const userId = user?.id || getOrCreateGuestId();

      const { error: queueError } = await supabase
        .from("waiting_room_queue")
        .insert({
          meeting_id: meetingDetails.id,
          user_id: userId,
          guest_name: !user ? guestName.trim() : null,
          status: "waiting",
        });

      if (queueError) {
        throw queueError;
      }

      subscribeToAdmission(meetingDetails.id, userId, nextPath);
      pollAdmission(meetingDetails.id, userId, nextPath);
    } else {
      setIsWaiting(false);
      navigate(nextPath);
    }
  };

  const startActivationPolling = (meetingDetails: MeetingLookup, normalizedCode: string) => {
    if (activationPollId) {
      clearInterval(activationPollId);
    }

    const interval = setInterval(async () => {
      const { data, error } = await fetchMeetingByCode(normalizedCode);

      if (error) {
        console.error("Error polling meeting activation:", error);
        return;
      }

      if (data?.is_active) {
        clearInterval(interval);
        setActivationPollId(null);
        setWaitingMessage(t("joinMeeting.hostStartedMessage"));
        try {
          await proceedToMeeting({
            ...meetingDetails,
            ...data,
            title: data.title ?? meetingDetails.title,
          });
        } catch (joinError) {
          console.error("Error joining after activation:", joinError);
          toast({
            title: t("joinMeeting.errors.joinFailedTitle"),
            description: t("joinMeeting.errors.joinFailedDesc"),
            variant: "destructive",
          });
          setLoading(false);
        }
      }
    }, 4000);

    setActivationPollId(interval);
  };

  const handleJoinMeeting = async () => {
    const errors: Record<string, string> = {};

    if (!meetingCode.trim()) {
      errors.meetingCode = t("joinMeeting.errors.meetingCodeRequired");
    } else if (!isValidMeetingCode(meetingCode)) {
      errors.meetingCode = t("joinMeeting.errors.meetingCodeInvalid");
    }

    if (!user && !guestName.trim()) {
      errors.guestName = t("joinMeeting.errors.guestNameRequired");
    }

    setInlineErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      const normalizedCode = meetingCode.trim().toUpperCase();

      const { data: meetingInfo, error: meetingLookupError } = await fetchMeetingByCode(normalizedCode);

      if (meetingLookupError) {
        console.error("Meeting lookup error:", meetingLookupError);
        setInlineErrors({ meetingCode: t("joinMeeting.errors.lookupFailed") });
        setLoading(false);
        return;
      }

      if (!meetingInfo) {
        setInlineErrors({ meetingCode: t("joinMeeting.errors.notFoundInline") });
        setLoading(false);
        return;
      }

      if (meetingInfo.has_password && !password) {
        setInlineErrors({ password: t("joinMeeting.errors.passwordRequired") });
        setLoading(false);
        return;
      }

      if (meetingInfo.has_password) {
        const { data: verification, error: verificationError } = await supabase
          .rpc('verify_meeting_password', {
            _meeting_code: meetingInfo.meeting_code,
            _password: password,
          })
          .maybeSingle();

        if (verificationError) {
          console.error("Meeting verification error:", verificationError);
          setInlineErrors({ meetingCode: t("joinMeeting.errors.verificationFailed") });
          setLoading(false);
          return;
        }

        if (!verification?.is_valid) {
          setInlineErrors({ password: t("joinMeeting.errors.passwordIncorrect") });
          setLoading(false);
          return;
        }
      }

      const meetingDetails: MeetingLookup = {
        id: meetingInfo.id,
        title: meetingInfo.title,
        meeting_code: meetingInfo.meeting_code,
        waiting_room_enabled: meetingInfo.waiting_room_enabled,
        is_active: meetingInfo.is_active,
        has_password: meetingInfo.has_password,
        scheduled_start: meetingInfo.scheduled_start,
      };

      if (!meetingDetails.is_active) {
        setInlineErrors({});
        setWaitingTitle(t("joinMeeting.waitingForHostTitle"));
        setIsWaiting(true);
        setWaitingStatus({ meetingId: meetingDetails.id, meetingTitle: meetingDetails.title || t("joinMeeting.meetingLabel"), meetingCode: meetingDetails.meeting_code });
        setWaitingMessage(meetingDetails.scheduled_start
          ? t("joinMeeting.scheduledWaitMessage", {
            date: new Date(meetingDetails.scheduled_start).toLocaleString(),
          })
          : t("joinMeeting.notStartedMessage"));
        setLoading(false);
        startActivationPolling(meetingDetails, normalizedCode);
        return;
      }

      await proceedToMeeting(meetingDetails);
    } catch (error: any) {
      // Check if this is a rate limit error
      const isRateLimit = handleRateLimitError(error, 'meeting_join');

      if (!isRateLimit) {
        const { title, description } = getJoinErrorDetails(error);
        toast({
          title,
          description,
          variant: "destructive",
          action: (
            <ToastAction altText={t("common.retry")} onClick={() => handleJoinMeeting()}>
              {t("common.retry")}
            </ToastAction>
          ),
        });
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-4 lg:p-8">
      {/* Animated background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-primary opacity-20 blur-3xl rounded-full animate-pulse" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-secondary opacity-20 blur-3xl rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      <div className="w-full max-w-2xl mx-auto relative z-10">
        <Card className="w-full shadow-card backdrop-blur-sm bg-card/95 border-border/50">
          <CardHeader className="text-center space-y-4 sm:space-y-6 pb-4 sm:pb-6 lg:pb-8">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-primary rounded-2xl blur-xl opacity-60 animate-pulse" />
                <div className="relative p-4 lg:p-5 bg-gradient-primary rounded-2xl shadow-glow">
                  <Video className="h-10 w-10 lg:h-12 lg:w-12 text-primary-foreground" />
                </div>
              </div>
            </div>

            <div className="space-y-1 sm:space-y-2">
              <CardTitle className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                {t("joinMeeting.title")}
              </CardTitle>
              <CardDescription className="text-sm sm:text-base lg:text-lg text-muted-foreground">
                {t("joinMeeting.subtitle")}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6 lg:space-y-10 px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-[1.05fr,0.95fr]">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="meetingCode" className="text-sm lg:text-base font-medium text-foreground">
                    {t("joinMeeting.meetingCodeLabel")}
                  </Label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Video className="h-5 w-5 lg:h-6 lg:w-6 text-muted-foreground" />
                    </div>
                    <Input
                      id="meetingCode"
                      placeholder={t("joinMeeting.meetingCodePlaceholder")}
                      value={meetingCode}
                      onChange={(e) => setMeetingCode(e.target.value.toUpperCase())}
                      disabled={!!codeFromUrl}
                      className="pl-12 lg:pl-14 h-12 lg:h-14 text-base lg:text-lg font-mono tracking-wider bg-muted/50 border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all hover:border-border/70"
                    />
                  </div>
                  {inlineErrors.meetingCode && (
                    <p className="text-sm text-destructive">{inlineErrors.meetingCode}</p>
                  )}
                </div>

                {!user && (
                  <div className="space-y-2">
                    <Label htmlFor="guestName" className="text-sm lg:text-base font-medium text-foreground flex items-center gap-2">
                      {t("joinMeeting.guestNameLabel")}
                      <span className="text-xs lg:text-sm text-destructive font-normal">*</span>
                    </Label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <Users className="h-5 w-5 lg:h-6 lg:w-6 text-muted-foreground" />
                      </div>
                      <Input
                        id="guestName"
                        placeholder={t("joinMeeting.guestNamePlaceholder")}
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        maxLength={50}
                        className="pl-12 lg:pl-14 h-12 lg:h-14 text-base lg:text-lg bg-muted/50 border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all hover:border-border/70"
                      />
                    </div>
                    {inlineErrors.guestName && (
                      <p className="text-sm text-destructive">{inlineErrors.guestName}</p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm lg:text-base font-medium text-foreground flex items-center gap-2">
                    {t("joinMeeting.passwordLabel")}
                    <span className="text-xs lg:text-sm text-muted-foreground font-normal">{t("joinMeeting.optionalLabel")}</span>
                  </Label>
                  <PasswordInput
                    id="password"
                    placeholder={t("joinMeeting.passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 lg:h-14 text-base lg:text-lg bg-muted/50 border-border/50 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all hover:border-border/70"
                  />
                </div>

                <div className="flex flex-wrap gap-2 items-center text-sm text-muted-foreground">
                  <Badge variant="secondary">{t("joinMeeting.fastJoinBadge")}</Badge>
                  <span>{t("joinMeeting.fastJoinText")}</span>
                </div>

                <Button
                  onClick={handleJoinMeeting}
                  className="w-full h-12 sm:h-14 lg:h-14 text-sm sm:text-base lg:text-lg font-semibold bg-gradient-primary hover:opacity-90 transition-all shadow-glow hover:shadow-stream hover:scale-[1.01]"
                  disabled={!meetingCode.trim() || loading || (!user && !guestName.trim())}
                >
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 lg:h-5 lg:w-5 mr-2" />
                  {loading ? t("joinMeeting.joining") : user ? t("joinMeeting.joinButton") : t("joinMeeting.joinAsGuest")}
                </Button>

                {meetingCode.trim() && isValidMeetingCode(meetingCode) && (
                  <JoinAIPrep meetingCode={meetingCode.trim().toUpperCase()} />
                )}
              </div>

              <div className="space-y-3 rounded-lg border border-border/60 bg-muted/40 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-primary" />
                    <p className="text-sm font-medium">{t("joinMeeting.preview.title")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={isMuted ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => setIsMuted((prev) => !prev)}
                    >
                      {isMuted ? t("joinMeeting.preview.unmute") : t("joinMeeting.preview.mute")}
                    </Button>
                    <Button
                      variant={isVideoOn ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => setIsVideoOn((prev) => !prev)}
                    >
                      {isVideoOn ? t("joinMeeting.preview.cameraOff") : t("joinMeeting.preview.cameraOn")}
                    </Button>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-lg border border-border/60 bg-black/70 relative overflow-hidden">
                  <video ref={videoRef} className="h-full w-full object-contain" muted playsInline />
                  {!isVideoOn && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/80 text-muted-foreground">
                      <Video className="h-8 w-8" />
                      <p className="text-sm">{t("joinMeeting.preview.cameraPaused")}</p>
                    </div>
                  )}
                </div>
                {inlineErrors.preview && <p className="text-sm text-destructive">{inlineErrors.preview}</p>}
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div className="rounded-md border border-border/50 bg-background/60 p-2">
                    <p className="font-semibold text-foreground">{t("joinMeeting.preview.micLabel")}</p>
                    <p>{isMuted ? t("joinMeeting.preview.micMuted") : t("joinMeeting.preview.micReady")}</p>
                  </div>
                  <div className="rounded-md border border-border/50 bg-background/60 p-2">
                    <p className="font-semibold text-foreground">{t("joinMeeting.preview.cameraLabel")}</p>
                    <p>{isVideoOn ? t("joinMeeting.preview.cameraLive") : t("joinMeeting.preview.cameraOffDefault")}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center pt-2 pb-2">
              <Button
                variant="ghost"
                onClick={() => navigate(user ? "/dashboard" : "/")}
                className="text-muted-foreground hover:text-foreground transition-colors text-sm lg:text-base"
              >
                {user ? t("joinMeeting.backToDashboard") : t("joinMeeting.backToHome")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {isWaiting && waitingStatus && (
        <div className="fixed inset-0 bg-background/90 backdrop-blur-sm flex items-center justify-center p-4 z-20">
          <Card className="max-w-lg w-full shadow-glow">
            <CardHeader className="text-center space-y-2">
              <div className="flex justify-center">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock3 className="h-7 w-7 text-primary animate-pulse" />
                </div>
              </div>
              <CardTitle>{waitingTitle}</CardTitle>
              <CardDescription>
                {waitingMessage || t("joinMeeting.waitingScreenMessage")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-border/60 bg-muted/40 p-4 space-y-2">
                <p className="text-sm font-semibold">{t("joinMeeting.meetingLabel")}</p>
                <p className="text-lg font-bold">{waitingStatus.meetingTitle}</p>
                <p className="text-xs text-muted-foreground">
                  {t("joinMeeting.meetingCodeShort", { code: waitingStatus.meetingCode || meetingCode })}
                </p>
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/40 p-4 space-y-2">
                <p className="text-sm font-semibold">{t("joinMeeting.statusLabel")}</p>
                <p className="text-sm text-muted-foreground">{waitingMessage}</p>
              </div>
              {waitingError && <p className="text-sm text-destructive">{waitingError}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => navigate(user ? "/dashboard" : "/")}>
                  {t("common.cancel")}
                </Button>
                <Button onClick={handleJoinMeeting} disabled={loading}>
                  {t("common.retry")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

